import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  CircleAlert, Clock, Info, Receipt, TrendingDown, TrendingUp, Wallet,
} from 'lucide-react';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import { getCourseRevenue, getCoursePayments } from '../../api/revenue';
import { unwrapList, unwrapMeta } from '../../utils/api';
import {
  formatDateTime, formatMoney, formatMoneyCompact, formatMonthYear, formatNumber, formatPercent,
  formatShortDate,
} from '../../utils/datetime';
import { usePagination } from '../../hooks/usePagination';
import { useCourseTitle } from '../../hooks/useCourseTitle';
import Avatar from '../../components/Avatar/Avatar';
import Pagination from '../../components/Pagination/Pagination';
import StatusBadge from '../../components/StatusBadge/StatusBadge';
import TableCard from '../../components/TableCard/TableCard';
import LoadingState from '../../components/LoadingState/LoadingState';
import PageEmptyState from '../../components/PageEmptyState/PageEmptyState';
import MetricCard from '../../components/MetricCard/MetricCard';
import FilterSheet from '../../components/FilterSheet/FilterSheet';
import PageHead from '../../components/PageHead/PageHead';
import Dropdown from '../../components/Dropdown/Dropdown';
import DateRangePicker from '../../components/DatePicker/DateRangePicker';
import {
  BAR_CURSOR_PROPS, CHART_MARGIN, ChartFrame, ChartLegend, ChartTooltip, GRID_PROPS, PIE_PROPS,
  X_AXIS_PROPS, Y_AXIS_PROPS, barProps, compactTick, seriesColor,
} from '../../components/charts/chartTheme';
import { useI18n } from '../../i18n/I18nContext';
import './CourseRevenue.css';

const MONTH_KEY = /^(\d{4})-(\d{2})/;
// "2026-09" -> { year: 2026, index: 8 }; anything else is shown as it came.
const parseMonth = (key) => {
  const match = MONTH_KEY.exec(String(key ?? ''));
  return match ? { year: Number(match[1]), index: Number(match[2]) - 1 } : null;
};
// The axis names the month short ("Sen": six labels still fit a phone); the tooltip names it in
// full with the year ("Sentabr 2026"). The short name is the date helper's own, minus its day.
const monthTick = (key) => {
  const month = parseMonth(key);
  if (!month) return String(key ?? '');
  const short = formatShortDate(`${month.year}-${String(month.index + 1).padStart(2, '0')}-01`)
    .replace(/^\d+[-\s]\s*/, '');
  return short.charAt(0).toUpperCase() + short.slice(1);
};
const monthFull = (key) => {
  const month = parseMonth(key);
  return month ? formatMonthYear(month.year, month.index) : String(key ?? '');
};

// The API lists only the months that had a payment. A chart that jumps from April to June reads
// as two neighbours, so a quiet month in between is filled in as a zero bar. Rows the helper does
// not understand (a key that is not "YYYY-MM") are left exactly as they came.
function fillQuietMonths(rows) {
  const parsed = rows.map((row) => ({ row, month: parseMonth(row?.month) }));
  if (parsed.length < 2 || parsed.some(({ month }) => !month)) return rows;
  const ordinal = ({ year, index }) => year * 12 + index;
  const byOrdinal = new Map(parsed.map(({ row, month }) => [ordinal(month), row]));
  const first = Math.min(...byOrdinal.keys());
  const last = Math.max(...byOrdinal.keys());
  const filled = [];
  for (let at = first; at <= last; at += 1) {
    const year = Math.floor(at / 12);
    const key = `${year}-${String((at % 12) + 1).padStart(2, '0')}`;
    filled.push(byOrdinal.get(at) || { month: key, amount: 0 });
  }
  return filled;
}

// A provider keeps its colour in the donut, its legend and the payment table.
const METHOD_ORDER = ['PAYCOM', 'CLICK'];
const methodColor = (method) => {
  const at = METHOD_ORDER.indexOf(String(method || '').toUpperCase());
  return seriesColor(at >= 0 ? at : METHOD_ORDER.length);
};

function CourseRevenue({ courseId: courseIdProp, embedded: embeddedProp } = {}) {
  const { t } = useI18n();
  const params = useParams();
  const courseId = courseIdProp || params.courseId || params.id;
  // Inside the course workspace the tab already names the page; the standalone route carries
  // its own header.
  const embedded = embeddedProp ?? Boolean(courseIdProp);
  const courseTitle = useCourseTitle(embedded ? null : courseId);
  const [revenue, setRevenue] = useState(null);
  // The revenue summary (KPIs and charts) is ADMIN-only on the API; other roles get a 403 for it
  // and still see the payment history.
  const [summaryLocked, setSummaryLocked] = useState(false);
  const [payments, setPayments] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [denied, setDenied] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [method, setMethod] = useState('');
  const [status, setStatus] = useState('');
  const pagination = usePagination({ limit: 10 });
  const { page: paginationPage, limit: paginationLimit, applyMeta, reset: paginationReset } = pagination;

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [revenueResult, paymentsResult] = await Promise.allSettled([
        getCourseRevenue(courseId),
        getCoursePayments(courseId, {
          limit: paginationLimit,
          offset: (paginationPage - 1) * paginationLimit,
          ...(startDate && { startDate }),
          ...(endDate && { endDate }),
          ...(method && { method }),
          ...(status && { status }),
        }),
      ]);

      // The payment history is the tab's core: without it the tab errors.
      if (paymentsResult.status === 'rejected') throw paymentsResult.reason;
      const paymentsRes = paymentsResult.value;

      // API shape: { total, payments: [...] }
      const list = Array.isArray(paymentsRes?.payments) ? paymentsRes.payments : unwrapList(paymentsRes);
      setPayments(list);
      setDenied(false);
      const total = Number(paymentsRes?.total ?? unwrapMeta(paymentsRes, { limit: paginationLimit }).total ?? list.length);
      applyMeta({ total, totalPages: Math.max(1, Math.ceil(total / paginationLimit)) });

      if (revenueResult.status === 'fulfilled') {
        setRevenue(revenueResult.value);
        setSummaryLocked(false);
        setError(null);
      } else if (revenueResult.reason?.code === 403) {
        setRevenue(null);
        setSummaryLocked(true);
        setError(null);
      } else {
        setRevenue(null);
        setError(t('courseCommerce.revenue.errorFailedToLoad'));
      }
      setLoaded(true);
    } catch (err) {
      setDenied(err.code === 403);
      setError(err.code === 403
        ? t('courseCommerce.revenue.accessDenied')
        : t('courseCommerce.revenue.errorFailedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [
    courseId,
    paginationPage,
    paginationLimit,
    startDate,
    endDate,
    method,
    status,
    applyMeta,
    t,
  ]);

  useEffect(() => {
    paginationReset();
  }, [startDate, endDate, method, status, paginationReset]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleClearFilters = () => {
    setStartDate('');
    setEndDate('');
    setMethod('');
    setStatus('');
  };

  // GET /enrollments/courses/:id/revenue: byMonth [{ month: 'YYYY-MM', amount }], byMethod { paycom, click }
  const chartData = fillQuietMonths(revenue?.byMonth || []);
  const paymentMethods = [
    { method: 'PAYCOM', amount: revenue?.byMethod?.paycom || 0 },
    { method: 'CLICK', amount: revenue?.byMethod?.click || 0 },
  ].filter((m) => m.amount > 0);
  const hasFilters = Boolean(startDate || endDate || method || status);
  // The date range counts once, however many of its two ends are set
  const activeFilters = [startDate || endDate, method, status].filter(Boolean).length;

  // Translated label for the payment status pill.
  // Payment providers write status in mixed case; we normalise to lowercase
  // before matching so the display is never a raw untranslated value.
  // Paycom:  'created' | 'PROCESSING' | 'completed' | 'cancelled'
  // Click:   'prepared' | 'PROCESSING' | 'completed' | 'failed' | 'FAILED' | 'cancelled'
  // Buckets: created/prepared → pending, PROCESSING → processing,
  //          completed → completed, failed/FAILED → failed, cancelled → cancelled
  const getPaymentStatus = (rawStatus) => {
    const normalized = String(rawStatus || '').toLowerCase();
    switch (normalized) {
      case 'completed':
        return { tone: 'success', label: t('courseCommerce.revenue.statusCompleted') };
      case 'processing':
        return { tone: 'info', label: t('courseCommerce.revenue.statusProcessing') };
      case 'failed':
        return { tone: 'danger', label: t('courseCommerce.revenue.statusFailed') };
      case 'cancelled':
        return { tone: 'danger', label: t('courseCommerce.revenue.statusCancelled') };
      default:
        // created / prepared are pre-payment initialisation states; anything unknown reads as
        // pending too, never as a raw provider string.
        return { tone: 'warning', label: t('courseCommerce.revenue.statusPending') };
    }
  };

  // The backend returns the raw Prisma `PaymentProvider` enum value (uppercase 'PAYCOM' / 'CLICK');
  // show the brand names the filter uses ("Payme", "Click"), not the enum.
  const getPaymentMethodLabel = (rawMethod) => {
    const normalized = String(rawMethod || '').toUpperCase();
    switch (normalized) {
      case 'PAYCOM':
        return t('courseCommerce.revenue.methodPaycom');
      case 'CLICK':
        return t('courseCommerce.revenue.methodClick');
      default:
        return rawMethod || '—';
    }
  };

  // Donut slices carry the brand name, never the raw provider enum
  const methodSlices = paymentMethods.map((m) => ({ ...m, name: getPaymentMethodLabel(m.method) }));

  const growth = Number(revenue?.monthlyGrowth) || 0;
  // The API answers 0 when last month had no revenue to compare with: that is "nothing to compare",
  // not a flat month, so the tile stays neutral instead of reading as good news.
  const noBaseline = growth === 0;
  const money = (value) => formatMoney(value, 'UZS', { free: false });

  // Values match the backend's Prisma `PaymentProvider` enum exactly (PAYCOM/CLICK) — the service
  // casts this straight to the enum type, so a lowercase value throws a 500.
  const methodOptions = [
    { value: '', label: t('courseCommerce.revenue.allMethods') },
    { value: 'PAYCOM', label: t('courseCommerce.revenue.methodPaycom') },
    { value: 'CLICK', label: t('courseCommerce.revenue.methodClick') },
  ];
  // Filter values must match what providers actually write to the DB. 'pending' is never written
  // by any provider — removed. 'PROCESSING' is stored uppercase by both Paycom and Click.
  const statusOptions = [
    { value: '', label: t('courseCommerce.revenue.allStatuses') },
    { value: 'completed', label: t('courseCommerce.revenue.statusCompleted') },
    { value: 'PROCESSING', label: t('courseCommerce.revenue.statusProcessing') },
    { value: 'failed', label: t('courseCommerce.revenue.statusFailed') },
    { value: 'cancelled', label: t('courseCommerce.revenue.statusCancelled') },
  ];
  const header = !embedded && (
    <PageHead
      back={{ to: `/courses/edit/${courseId}?tab=revenue`, label: t('courseCommerce.common.backToCourse') }}
      eyebrow={<span className="rev-eyebrow">{courseTitle || t('courseCommerce.common.parent')}</span>}
      title={t('courseCommerce.revenue.title')}
      description={t('courseCommerce.revenue.subtitle')}
    />
  );

  if (loading && !loaded) {
    return (
      <div className="ui-stack rev-page">
        {header}
        <LoadingState skeleton="page" message={t('courseCommerce.revenue.loading')} />
      </div>
    );
  }

  if (!loading && !loaded && error) {
    return (
      <div className="ui-stack rev-page">
        {header}
        <PageEmptyState
          variant={denied ? 'forbidden' : 'error'}
          size="lg"
          description={error}
          onRetry={denied ? undefined : fetchData}
        />
      </div>
    );
  }

  // Nobody has paid yet and no filter is hiding anything: one calm empty state, not two empty
  // charts and an empty history.
  const nothingPaid = loaded && payments.length === 0 && !hasFilters && !error;

  let history;
  if (payments.length === 0) {
    if (error) {
      history = (
        <PageEmptyState
          variant="error"
          description={error}
          onRetry={fetchData}
        />
      );
    } else if (hasFilters) {
      history = (
        <PageEmptyState
          variant="search"
          title={t('courseCommerce.revenue.filteredEmptyTitle')}
          description={t('courseCommerce.revenue.filteredEmptyDescription')}
          actionLabel={t('courseCommerce.common.clearFilters')}
          onAction={handleClearFilters}
        />
      );
    } else {
      history = null;
    }
  } else {
    history = (
      <TableCard
        className="rev-table-card"
        aria-label={t('courseCommerce.revenue.paymentHistoryTitle')}
        footer={(
          <div className="rev-foot">
            <span className="rev-count">{t('courseCommerce.revenue.kpiPaymentsMeta', { count: formatNumber(pagination.total, { decimals: 0 }) })}</span>
            <Pagination state={pagination} hideOnSinglePage />
          </div>
        )}
      >
        <table className="ui-table ui-table--stack rev-table">
          <thead>
            <tr>
              <th>{t('courseCommerce.revenue.tableStudent')}</th>
              <th className="ui-num">{t('courseCommerce.revenue.tableAmount')}</th>
              <th>{t('courseCommerce.revenue.tableMethod')}</th>
              <th>{t('courseCommerce.revenue.tableDate')}</th>
              <th>{t('courseCommerce.revenue.tableStatus')}</th>
            </tr>
          </thead>
          <tbody>
            {payments.map((payment) => {
              const paymentStatus = getPaymentStatus(payment.status);
              const who = payment.studentName || payment.studentEmail || '';
              return (
                <tr key={payment.paymentId}>
                  <td>
                    <div className="ui-cell-person">
                      <Avatar name={who} id={payment.studentEmail || who} />
                      <span className="ui-cell-person__text">
                        <span className="ui-cell-person__name">{who || '—'}</span>
                        {payment.studentName && payment.studentEmail && (
                          <span className="ui-cell-person__sub">{payment.studentEmail}</span>
                        )}
                      </span>
                    </div>
                  </td>
                  <td className="ui-num" data-label={t('courseCommerce.revenue.tableAmount')}>
                    <span className="rev-amount">{money(payment.amount)}</span>
                  </td>
                  <td data-label={t('courseCommerce.revenue.tableMethod')}>
                    <span className="rev-method" style={{ '--ui-dot': methodColor(payment.method) }}>
                      {getPaymentMethodLabel(payment.method)}
                    </span>
                  </td>
                  <td className="rev-muted rev-date" data-label={t('courseCommerce.revenue.tableDate')}>
                    {payment.createdAt ? formatDateTime(payment.createdAt) : '—'}
                  </td>
                  <td data-label={t('courseCommerce.revenue.tableStatus')}>
                    <StatusBadge tone={paymentStatus.tone}>{paymentStatus.label}</StatusBadge>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </TableCard>
    );
  }

  return (
    <div className="ui-stack rev-page">
      {header}

      {error && payments.length > 0 && (
        <div className="ui-alert" role="alert">
          <CircleAlert aria-hidden="true" />
          <p>{error}</p>
          <button type="button" className="ds-btn ds-btn-secondary ds-btn-sm" onClick={fetchData}>
            {t('ui.retry')}
          </button>
        </div>
      )}

      {summaryLocked && (
        <div className="ui-alert" data-tone="info" role="note">
          <Info aria-hidden="true" />
          <p>{t('courseCommerce.revenue.summaryAdminOnly')}</p>
        </div>
      )}

      {/* KPI tiles */}
      {!summaryLocked && (
        <div className="kpi-row rev-kpis">
          <MetricCard
            className="rev-kpi--money"
            title={t('courseCommerce.revenue.kpiTotalRevenue')}
            value={formatMoneyCompact(revenue?.totalRevenue)}
            valueTitle={money(revenue?.totalRevenue)}
            change={t('courseCommerce.revenue.kpiPaymentsMeta', { count: revenue?.paymentCount || 0 })}
            icon={<Wallet />}
            tone="brand"
          />
          <MetricCard
            className="rev-kpi--money"
            title={t('courseCommerce.revenue.kpiAveragePayment')}
            value={formatMoneyCompact(revenue?.averagePayment)}
            valueTitle={money(revenue?.averagePayment)}
            change={t('courseCommerce.revenue.kpiPerTransaction')}
            icon={<Receipt />}
            tone="info"
          />
          <MetricCard
            title={t('courseCommerce.revenue.kpiPending')}
            value={String(revenue?.pendingCount || 0)}
            change={formatMoneyCompact(revenue?.pendingAmount)}
            icon={<Clock />}
            tone="coral"
          />
          <MetricCard
            title={t('courseCommerce.revenue.kpiMonthlyGrowth')}
            value={noBaseline ? '—' : `${growth > 0 ? '+' : ''}${formatPercent(growth)}`}
            change={noBaseline ? t('courseCommerce.revenue.kpiNoBaseline') : t('courseCommerce.revenue.kpiVsLastMonth')}
            icon={growth < 0 ? <TrendingDown /> : <TrendingUp />}
            tone={noBaseline ? 'neutral' : growth > 0 ? 'success' : 'coral'}
          />
        </div>
      )}

      {nothingPaid ? (
        <PageEmptyState
          variant="revenue"
          size="sm"
          title={t('courseCommerce.revenue.emptyTitle')}
          description={t('courseCommerce.revenue.emptyDescription')}
        />
      ) : (
        <>
          {/* Charts */}
          {!summaryLocked && (
            <div className="rev-charts">
              <section className="section-card">
                <div className="section-head">
                  <div>
                    <h2 className="section-head__title">{t('courseCommerce.revenue.chartRevenueTrend')}</h2>
                    <p className="section-head__desc">{t('courseCommerce.revenue.chartRevenueDesc')}</p>
                  </div>
                </div>
                <ChartFrame data={chartData} minPoints={1} emptyTitle={t('courseCommerce.revenue.chartNoRevenueData')}>
                  <BarChart data={chartData} margin={CHART_MARGIN}>
                    <CartesianGrid {...GRID_PROPS} />
                    <XAxis dataKey="month" {...X_AXIS_PROPS} minTickGap={8} tickFormatter={monthTick} />
                    <YAxis {...Y_AXIS_PROPS} width={72} tickFormatter={compactTick} />
                    <Tooltip
                      cursor={BAR_CURSOR_PROPS}
                      content={<ChartTooltip valueFormatter={money} labelFormatter={monthFull} />}
                    />
                    <Bar
                      dataKey="amount"
                      name={t('courseCommerce.revenue.chartRevenueSeriesName')}
                      {...barProps(0)}
                    />
                  </BarChart>
                </ChartFrame>
              </section>

              <section className="section-card">
                <div className="section-head">
                  <div>
                    <h2 className="section-head__title">{t('courseCommerce.revenue.chartPaymentMethods')}</h2>
                  </div>
                </div>
                <div className="rev-donut">
                  <ChartFrame
                    data={methodSlices}
                    minPoints={1}
                    className="rev-donut-chart"
                    emptyTitle={t('courseCommerce.revenue.chartNoPaymentData')}
                  >
                    <PieChart>
                      <Pie data={methodSlices} {...PIE_PROPS} dataKey="amount" nameKey="name">
                        {methodSlices.map((slice) => (
                          <Cell key={slice.method} fill={methodColor(slice.method)} />
                        ))}
                      </Pie>
                      <Tooltip content={<ChartTooltip valueFormatter={money} />} />
                    </PieChart>
                  </ChartFrame>
                  {methodSlices.length > 0 && (
                    <ChartLegend
                      items={methodSlices.map((slice) => ({
                        label: `${slice.name} · ${formatMoneyCompact(slice.amount)}`,
                        color: methodColor(slice.method),
                      }))}
                    />
                  )}
                </div>
              </section>
            </div>
          )}

          {/* Payment history: toolbar, then the table card */}
          <section className="rev-history">
            <div className="section-head">
              <div>
                <h2 className="section-head__title">{t('courseCommerce.revenue.paymentHistoryTitle')}</h2>
              </div>
              {hasFilters && (
                <button type="button" className="section-head__action" onClick={handleClearFilters}>
                  {t('courseCommerce.revenue.clearButton')}
                </button>
              )}
            </div>

            <div className="rev-filters">
              {/* From 640px the controls sit in this row; on a phone they fold into one "Filtrlar" button and a sheet */}
              <FilterSheet count={activeFilters} onClear={handleClearFilters}>
                <div className="rev-filter">
                  <span className="ui-label rev-filter-label">{t('courseCommerce.revenue.filterDates')}</span>
                  <DateRangePicker
                    from={startDate}
                    to={endDate}
                    onChange={({ from, to }) => { setStartDate(from); setEndDate(to); }}
                    fromLabel={t('courseCommerce.revenue.startDateAriaLabel')}
                    toLabel={t('courseCommerce.revenue.endDateAriaLabel')}
                  />
                </div>
                <div className="rev-filter">
                  <span className="ui-label rev-filter-label">{t('courseCommerce.revenue.filterMethod')}</span>
                  <Dropdown
                    value={method}
                    onChange={setMethod}
                    options={methodOptions}
                    ariaLabel={t('courseCommerce.revenue.methodFilterAriaLabel')}
                  />
                </div>
                <div className="rev-filter">
                  <span className="ui-label rev-filter-label">{t('courseCommerce.revenue.tableStatus')}</span>
                  <Dropdown
                    value={status}
                    onChange={setStatus}
                    options={statusOptions}
                    ariaLabel={t('courseCommerce.revenue.statusFilterAriaLabel')}
                  />
                </div>
              </FilterSheet>
            </div>

            <div className="rev-results" aria-busy={loading}>
              {history}
            </div>
          </section>
        </>
      )}
    </div>
  );
}

export default CourseRevenue;
